import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  children?: React.ReactNode;
};

const readStoredUser = (): Record<string, any> | null => {
  try {
    const raw = localStorage.getItem('edu_user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** 展示名：兼容仅 id、无 username 等异常数据，避免下拉区一直 Loading 无法退出 */
const resolveDisplayName = (u: Record<string, any> | null | undefined) => {
  if (!u) return '';
  return (
    u.username ||
    u.mobile ||
    u.name ||
    u.phone ||
    (u.id != null ? `用户#${u.id}` : '') ||
    '已登录'
  );
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const u = initialState?.currentUser || readStoredUser();
  const displayName = resolveDisplayName(u);
  return <span className="anticon">{displayName}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ children }) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      localStorage.removeItem('edu_user');
      localStorage.removeItem('edu_token');
    } catch {
      // ignore
    }
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    const redirect = urlParams.get('redirect');
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState, loading: initialLoading } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    // 当前项目未单独做「个人中心/设置」页，先回到欢迎页（避免跳到不存在的 /account/*）
    if (key === 'center' || key === 'settings') {
      history.push('/welcome');
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  const currentUser = initialState?.currentUser || readStoredUser();
  const displayName = resolveDisplayName(currentUser);

  // 有本地登录态即可出菜单；避免 initialState 未同步时顶栏一直转圈
  if (initialLoading && !currentUser) {
    return loading;
  }

  if (!currentUser) {
    return loading;
  }

  const menuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      trigger={['click', 'hover']}
      placement="bottomRight"
      /** 顶栏常有 overflow，挂到 body 避免菜单被裁切且点击无反馈 */
      getPopupContainer={() => document.body}
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children ?? (
        <span className={styles.action}>
          <UserOutlined style={{ marginRight: 8 }} />
          <span>{displayName}</span>
        </span>
      )}
    </HeaderDropdown>
  );
};
